// src/components/nexilink/empty-state.tsx
// Domain-specific empty states built on shadcn/ui components
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ActionButton } from './action-button';

interface EmptyStateProps {
    icon: LucideIcon;
    title: string;
    description: string;
    action?: {
        label: string;
        onClick: () => void;
        icon?: LucideIcon;
    };
    className?: string;
}

export function EmptyState({
    icon: Icon,
    title,
    description,
    action,
    className
}: EmptyStateProps) {
    return (
        <div className={cn(
            'p-12 bg-white rounded-2xl border-2 border-dashed border-gray-200 text-center',
            className
        )}>
            <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2]/30 rounded-full flex items-center justify-center">
                    <Icon className="w-8 h-8 text-[#1A4D4F]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {title}
                </h3>
                <p className="text-gray-500 mb-6">
                    {description}
                </p>
                {action && (
                    <ActionButton
                        icon={action.icon}
                        onClick={action.onClick}
                        size="lg"
                    >
                        {action.label}
                    </ActionButton>
                )}
            </div>
        </div>
    );
}

// Loading State
interface LoadingStateProps {
    message?: string;
    className?: string;
}

export function LoadingState({
    message = 'Laddar...',
    className
}: LoadingStateProps) {
    return (
        <div className={cn('flex items-center justify-center py-16', className)}>
            <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-muted-foreground font-medium">{message}</p>
            </div>
        </div>
    );
}

// Error State
interface ErrorStateProps {
    title?: string;
    message: string;
    onRetry?: () => void;
    className?: string;
}

export function ErrorState({
    title = 'Ett fel uppstod',
    message,
    onRetry,
    className
}: ErrorStateProps) {
    return (
        <div className={cn(
            'bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-4',
            className
        )}>
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">{title}</h3>
                <p className="text-sm text-red-700 mb-2">{message}</p>
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="text-sm text-red-800 underline hover:text-red-900 font-medium"
                    >
                        Försök igen
                    </button>
                )}
            </div>
        </div>
    );
}
